import { createContext, useContext, useEffect, useState } from "react";
import CarSelection from "../../forms/test-drive/car-selection";
import { Formik, Form } from "formik";
import TestDrivePersonalia from "../../forms/test-drive/personalia";
import {
  DealershipResponsibilityArea,
  ExploreUrl,
} from "../../../../common/typedefs";
import * as Yup from "yup";
import { isObject } from "lodash";
import { FieldErrorText } from "../../forms/test-drive/personalia/styles";
import Receipt from "../../components/receipt";
import { sendToGTM } from "../../lib/sendToGTM";
import { useLocation } from "react-router";
// @ts-ignore
import queryString from "query-string";
import { AllDataContext } from "../../lib/dataContext";
import { BestillPrøvekjøringLayout, PrøvekjøringOpel } from "./styles";
import { buildMaxCharMessage, yupPhoneValidation } from "../../lib/phoneValidation";

export interface CarInfo {
  chassis: string;
  fuel: string;
  imageUrl: string;
  image: any;
  carlineCode: string;
  carlineName: string;
  segment: string;
  modelSeries: string;
  receiptLinks: ExploreUrl[] | null;
}

export interface TestDrivePersonaliaValues {
  car: string;
  fname: string;
  lname: string;
  email: string;
  phone: string;
  message: string;
  newsletter: boolean;
  dealer: DealershipResponsibilityArea | string;
}

export const TestDriveContext = createContext({
  step: 0,
  setStep: (v: number) => {},
  selectedCarCode: "",
  setSelectedCarCode: (v: string) => {},
  selectedCar: "",
  setSelectedCar: (v: string) => {},
  setCarInfo: (v: any) => {},
});

export const TestDriveValidation = Yup.object().shape({
  fname: Yup.string().max(50, buildMaxCharMessage(50)).required("Påkrevd"),
  lname: Yup.string().max(100, buildMaxCharMessage(100)).required("Påkrevd"),
  email: Yup.string().max(100, buildMaxCharMessage(100)).email("Ugyldig epost").required("Påkrevd"),
  phone: yupPhoneValidation,
  dealer: Yup.object().required("Påkrevd"),
});

const TestDrivePage = () => {
  const { cars, testDriveTexts, brandCode } = useContext(AllDataContext);
  const [step, setStep] = useState<number>(0);
  const [selectedCar, setSelectedCar] = useState<string>("");
  const [selectedCarCode, setSelectedCarCode] = useState<string>("");
  const [carInfo, setCarInfo] = useState<CarInfo | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [trimCode, setTrimCode] = useState<string>("");

  const { search } = useLocation();
  const { modelid }: { modelid: string } = queryString.parse(search);
  const { trim }: { trim: string } = queryString.parse(search);

  const contextValue = {
    step,
    setStep,
    selectedCar,
    setSelectedCar,
    selectedCarCode,
    setSelectedCarCode,
    carInfo,
    setCarInfo,
  };

  useEffect(() => {
    if (trim) {
      setTrimCode(trim);
    }
  }, [trim]);

  useEffect(() => {
    if (modelid) {
      const filteredCars = cars.filter((car) => car.IsAvailableForTestDrive);
      const carFromQueryParam = filteredCars.find(
        (x) => x.carlineCode.toLowerCase() === modelid.toLowerCase()
      );

      if (carFromQueryParam) {
        setSelectedCar(carFromQueryParam.name);
        setCarInfo({
          chassis: carFromQueryParam.chassis,
          fuel: carFromQueryParam.fuel,
          imageUrl: carFromQueryParam.imageUrl,
          image: carFromQueryParam.image,
          carlineCode: carFromQueryParam.carlineCode,
          carlineName: carFromQueryParam.carlineName,
          segment: carFromQueryParam.segment,
          modelSeries: carFromQueryParam.modelseries,
          receiptLinks: carFromQueryParam.receiptLinks,
        });
        setStep(1);
      }
    }
  }, [modelid, cars]);

  const initialValues: TestDrivePersonaliaValues = {
    car: "",
    fname: "",
    lname: "",
    email: "",
    phone: "",
    message: "",
    newsletter: false,
    dealer: "",
  };

  return (
    <TestDriveContext.Provider value={contextValue}>
      <Formik
        validationSchema={TestDriveValidation}
        initialValues={initialValues}
        onSubmit={async (values, actions) => {
          if (isObject(values.dealer)) {
            setError(false);
            const data = JSON.stringify({
              dealerNo: values.dealer.DealerNo,
              firstName: values.fname,
              lastName: values.lname,
              newsletterConsent: values.newsletter,
              email: values.email,
              phone: values.phone,
              carlineCode: carInfo?.carlineCode,
              carlineName: carInfo?.carlineName,
              trimCode: trimCode,
              dealerEmail: values.dealer.Email,
            });
            try {
              setError(false);
              await fetch("api/boscrm/testdrive", {
                method: "post",
                headers: {
                  "Content-Type": "application/json",
                },
                body: data,
              });
              setStep(2);
              actions.setSubmitting(false);
            } catch (error) {
              sendToGTM({
                event: "error",
                field_name: "Send to BOSCRM | Testdrive",
                error_code: "400",
                error_text: error,
              });
            }
          } else {
            setError(true);
          }
        }}
      >
        {({ values, isSubmitting, errors, touched, isValid }) => (
          <Form>
            {error && (
              <FieldErrorText>
                Noe gikk galt, vennligst prøv igjen
              </FieldErrorText>
            )}
            {step === 0 && (
              <>
                {brandCode === "OPE" ? (
                  <BestillPrøvekjøringLayout>
                    <h1 className="title-h1-OpelLight">BESTILL</h1>
                    <PrøvekjøringOpel>
                      <h1 className="title-h1-OpelBold">PRØVEKJØRING</h1>
                      <img
                        alt="Slash.png"
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACMAAAAoCAYAAAB0HkOaAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAGuSURBVHgBxZjLUcMwEIZ/mcAkwyUHuDsd0AGUkBLSAXQAdEA6gA5CBbiEdEDOPIbcciDjZUNeq4ljW1rJ/i6xrId3/uibRDZQsqB+CnTeoWY5SKAkx8kDlCSg556Zz1TF/FC/zwtcQ4lBMl4XpaCL0yEvlUIBAdMz8zlVF8Nf0j2UGJjx/tqTBV3c8McbVNCsZ74H25Z3MgS6gxJOZWK3PQip88qibcsrmZA62/e8FtLrnMO8FKzrxoIuRyF07pmvDNpiQuts33cghs4Sp2Ry0AhK+IHZsb7aycTSWVI7mVg62/21F4qjs6SDGqx1phQq/jduVjaiZjJ6nflRj1UjKjdwbJ0llcnE1llSmkwTOktKk2lCZ3tseWd0nSVH1W5KZ0lJMs3oLClMZqNzChU062I5cZlRmEwonY2Zz13mHKjdtM6Sg2RC6Mx/K19dCyksJszZGU/wwNrAbegssZLhU+It1LjpbM3cXqx05nivoIDnsz2/GTzZFRNCZwOa+Gzc/Xy0q7MkWVfUCbBXkGkK2RXDG3cIJfnmVZiqmBBn55XO5+bD6XeosJi2dZb8Aef4sLEchV9aAAAAAElFTkSuQmCC"
                      />
                    </PrøvekjøringOpel>
                  </BestillPrøvekjøringLayout>
                ) : (
                  <h1 className="title-h1">BESTILL PRØVEKJØRING</h1>
                )}

                <CarSelection pageType="testdrive" />
              </>
            )}
            {step === 1 && (
              <TestDrivePersonalia
                submitting={isSubmitting}
                texts={testDriveTexts}
                isValid={isValid}
                errors={errors}
                touched={touched}
                carInfo={carInfo}
                pageType="testdrive"
              />
            )}
            {step === 2 && (
              <Receipt
                urls={testDriveTexts.exploreURLs}
                carUrls={carInfo?.receiptLinks ?? null}
                car={carInfo?.carlineName ? carInfo.carlineName.trim() : values.car}
                email={values.email}
                image={carInfo?.image}
                dealer={values.dealer as DealershipResponsibilityArea}
                receiptText={testDriveTexts.receiptText}
                brandCode={brandCode}
                carInfo={carInfo}
                formType="testdrive"
                newsletterConsented={values.newsletter}
              />
            )}
          </Form>
        )}
      </Formik>
    </TestDriveContext.Provider>
  );
};

export default TestDrivePage;
